//Standard libraries
import { useState, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
//Custom libraries
import { EMPTY_STRING, PROFILE_CREATION_TYPE } from "../../constants/common";
import formsUtil from "../../utils/formsUtil";
//UI
import { StyledGrid } from "@hcl-commerce-store-sdk/react-component";
import { StyledButton, StyledTextField, StyledTypography } from "../../styled-mui";
import * as userAction from "../../redux/actions/user";
import { useDispatch, useSelector } from "react-redux";
import getDisplayName from "react-display-name";
import {
  loginStatusSelector,
  registrationNewsletterLoadingSelector,
  registrationNewsletterStatusSelector,
} from "../../redux/selectors/user";
import { getReCaptchaToken } from "../../utils/storeLocatorUtils";
import { site } from "../../_foundation/constants/site";
import { useTranslation } from "react-i18next";
import AsyncCall from "../../_foundation/gtm/async.service";
import { CLICK } from "../../_foundation/constants/gtm";

interface RegistrationNewsletterContext {
  cid: string;
  type?: string;
  onSuccess?: () => void;
  onInput?: (email: string) => void;
  onError?: (error?: any) => void;
  onSubmit?: (email: string) => void;
}

function RegistrationNewsletter({
  cid,
  type,
  onSuccess,
  onInput,
  onError,
  onSubmit,
  ...props
}: RegistrationNewsletterContext) {
  const { t } = useTranslation();
  const widgetName = getDisplayName(RegistrationNewsletter);
  const registrationNewsletterStatus = useSelector(registrationNewsletterStatusSelector);
  const loading = useSelector(registrationNewsletterLoadingSelector);
  const [email, setEmail] = useState<string>(EMPTY_STRING);
  const loggedIn = useSelector(loginStatusSelector);
  const [errorEmail, setErrorEmail] = useState<boolean>(false);
  const controller = useMemo(() => new AbortController(), []);
  const dispatch = useDispatch();
  const googleApiKey = site.googleRecaptcha;
  const location = useLocation();
  const welcomeOfferPopin = type === "popin";
  const inputTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const handleSubmitReCaptcha = async (e) => {
    e.preventDefault();

    const token = await getReCaptchaToken("newsletter/footer");
    handleSubmit(token);
  };

  const payloadBase: any = {
    widget: widgetName,
    signal: controller.signal,
  };

  const checkEmail = () => {
    const isValid = formsUtil.validateEmail(email);
    setErrorEmail(!isValid);

    if (welcomeOfferPopin && !isValid) {
      onError?.({
        code: "VALIDATION_ERROR",
        message: "Email validation failed",
      });
    }
  };

  const resetMsg = () => {
    setTimeout(() => {
      dispatch(userAction.REGISTRATION_NEWSLETTER_RESET_ACTION({}));
      setErrorEmail(false);
    }, 4000);
  };

  const handleSubmit = (token) => {
    dispatch(userAction.REGISTRATION_NEWSLETTER_RESET_ACTION({}));

    if (welcomeOfferPopin) {
      onSubmit?.(email);
    }

    if (email.trim() !== EMPTY_STRING && formsUtil.validateEmail(email)) {
      const payload = {
        body: {
          email: email.toLowerCase(),
          type: PROFILE_CREATION_TYPE,
          statusEmail: true,
          source_optin: "LapeyreFR souscription NL",
          recaptchaToken: token,
          optin: false,
          isFooter: true,
          newsletterOn: true,
          isMyAccount: loggedIn ? true : false,
        },
        ...payloadBase,
      };
      // AsyncCall.sendClick(CLICK.NEWSLETTER, {
      //   btn_label: "a votre ecoute",
      //   page_type: "mon compte",
      //   content_group: "espace client",
      // });
      dispatch(userAction.REGISTRATION_NEWSLETTER_REQUEST_ACTION(payload));
      setErrorEmail(false);
    } else {
      setEmail("");

      if (welcomeOfferPopin) {
        onError?.({
          code: "INVALID_EMAIL",
          message: "Invalid email",
        });
      }

      setErrorEmail(true);
    }
  };

  useEffect(() => {
    if (registrationNewsletterStatus) {
      AsyncCall.sendClick(CLICK.NEWSLETTER, {
        btn_label: "a votre ecoute",
        page_type: "mon compte",
        content_group: "espace client",
      });

      setEmail("");
      resetMsg();

      if (welcomeOfferPopin || onSuccess) {
        onSuccess?.();
      }
    }
  }, [registrationNewsletterStatus]);

  useEffect(() => {
    setEmail("");
    setErrorEmail(false);
  }, [location]);

  useEffect(() => {
    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StyledGrid container px={welcomeOfferPopin ? 0 : { xs: 0, sm: 10 }}>
      {!welcomeOfferPopin && (
        <StyledGrid item xs={12}>
          <StyledTypography component="p" variant="h5" pb={2}>
            {t("Footer.Contact.Newsletter.Title")}
          </StyledTypography>
          <StyledTypography variant="body2" component="p" pb={4}>
            {t("Footer.Contact.Newsletter.Description")}
          </StyledTypography>
        </StyledGrid>
      )}

      <StyledGrid item xs={12}>
        <form noValidate name="registrationNewsletter" onSubmit={handleSubmitReCaptcha}>
          <StyledGrid
            container
            item
            columnSpacing={2}
            sx={{ flexDirection: welcomeOfferPopin ? "column" : "row" }}
            alignItems={welcomeOfferPopin ? "center" : "flex-start"}>
            <StyledGrid
              item
              className={welcomeOfferPopin && "welcomeOfferPopinInput"}
              xs={!welcomeOfferPopin}
              sx={{
                width: welcomeOfferPopin ? "100%" : "auto",
                p: welcomeOfferPopin ? "0 !important" : undefined,

                "&.welcomeOfferPopinInput .MuiFormHelperText-root.Mui-error": {
                  position: "absolute",
                },
              }}>
              <StyledTextField
                variant="outlined"
                welcomeOfferPopin={welcomeOfferPopin}
                required
                fullWidth
                success={registrationNewsletterStatus && !errorEmail}
                error={errorEmail}
                name="text"
                helperText={
                  errorEmail
                    ? t("error-message.NEWSLETTER_ADD_ERROR")
                    : registrationNewsletterStatus
                    ? t("success-message.NEWSLETTER_ADD_SUCCESS")
                    : ""
                }
                placeholder={welcomeOfferPopin ? "Email" : t("Footer.Contact.Newsletter.InputPlaceholder")}
                onChange={(e) => {
                  const value = e.target.value;
                  setEmail(value);

                  if (welcomeOfferPopin && onInput) {
                    clearTimeout(inputTimerRef.current);

                    inputTimerRef.current = setTimeout(() => {
                      onInput?.(value);
                    }, 500);
                  }
                }}
                onBlur={(e) => checkEmail()}
                value={email}
                inputProps={{
                  maxLength: 100,
                  type: "text",
                }}
              />
            </StyledGrid>
            <StyledGrid item>
              <StyledButton
                testId="sign-in-submit"
                variant="outline"
                color="black"
                type="submit"
                loading={loading}
                welcomeOfferPopin={welcomeOfferPopin}>
                {welcomeOfferPopin ? "je m’inscris" : t("Footer.Contact.Newsletter.Button")}
              </StyledButton>
            </StyledGrid>
          </StyledGrid>
          <div className="g-recaptcha" data-sitekey={googleApiKey} data-size="invisible"></div>
        </form>
      </StyledGrid>
    </StyledGrid>
  );
}

export { RegistrationNewsletter };
